import { useState, useEffect, useMemo } from 'react';
import App from './App';
import { Data } from './pages/Data';
import { AdminLayout } from './pages/Admin';
import { POSPage } from './pages/POS';
import { RedeemPage } from './pages/Redeem';
import { TenantRegistration } from './pages/TenantRegistration';
import { TenantSelect } from './pages/TenantSelect';
import { InviteAccept } from './pages/InviteAccept';
import { StaffSetup } from './pages/StaffSetup';
import { UserMenu } from './components/UserMenu';
import { SiDiscord, SiInstagram } from '@icons-pack/react-simple-icons';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useHasAdminAccess } from './hooks/useCurrentUserRole';
import { useBootstrapOnMount } from './hooks/useBootstrapOnMount';
import { useTenantsGetMe } from './api/posClient';
import toast from 'react-hot-toast';
import { normalizeImageUrl } from './utils/imageUrl';

/**
 * Logo 圖片組件
 * 顯示商家 Logo（如果有）和商家名稱
 * Logo 顯示在商家名稱左邊
 */
function LogoImage({ onNavigate }: { onNavigate: () => void }) {
  const [logoError, setLogoError] = useState(false);

  // 取得商家資訊以顯示商家名稱和 Logo
  const [token, setToken] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    const checkToken = () => {
      // 同時檢查 tenant_token 和 staff_session_token（支援兩種登入方式）
      const tenantToken = localStorage.getItem('tenant_token');
      const staffToken = localStorage.getItem('staff_session_token');
      setToken(tenantToken || staffToken);
      setTenantId(localStorage.getItem('tenant_id'));
    };

    checkToken();
    window.addEventListener('storage', checkToken);
    window.addEventListener('localStorageChange', checkToken);

    return () => {
      window.removeEventListener('storage', checkToken);
      window.removeEventListener('localStorageChange', checkToken);
    };
  }, []);

  // 檢查是否是 staff session（PIN 碼登入）
  // 注意：由於 Phase 1.1 已確保 token 互斥，這裡只需要檢查 staff_session_token 是否存在
  const isStaffSession = useMemo(() => {
    return !!localStorage.getItem('staff_session_token');
  }, [token]);

  const fetchOptions = useMemo(() => {
    if (!tenantId) return undefined;
    // Staff session 時使用 staff_session_token，member session 時使用 tenant_token
    const staffToken = localStorage.getItem('staff_session_token');
    const tenantToken = localStorage.getItem('tenant_token');
    const authToken = tenantToken || staffToken;

    if (!authToken) return undefined;

    return {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'X-Tenant-ID': tenantId,
      },
    } as RequestInit;
  }, [token, tenantId, isStaffSession]);

  // Staff session 時也應該嘗試調用 /api/tenants/me（後端應該允許 staff 查詢基本資訊）
  // 只要有 tenantId 和 token（無論是 staff_session_token 還是 tenant_token）就嘗試查詢
  // 重要：只有在 tenantId 和 fetchOptions 都存在時才發送請求，避免不必要的 401 錯誤
  const shouldFetch = !!tenantId && !!fetchOptions;
  const { data: tenantData } = useTenantsGetMe(
    shouldFetch && fetchOptions
      ? {
          fetch: fetchOptions,
          swr: {
            enabled: shouldFetch, // 明確設置 enabled，確保不會在沒有 token 時發送請求
            revalidateOnFocus: false,
            dedupingInterval: 300000, // 5 分鐘
          },
        }
      : {
          swr: {
            swrKey: null, // 使用 null key 完全禁用請求
            enabled: false, // 雙重保險：明確禁用
          },
        }
  );

  // 取得 tenant 資料
  const tenant =
    tenantData?.status === 200 && tenantData.data?.data
      ? (tenantData.data.data as { name: string; logo_url?: string | null })
      : undefined;

  // 當獲取到 tenant 名稱時，緩存到 localStorage（即使登出後也能顯示）
  useEffect(() => {
    if (tenant?.name && tenantId) {
      localStorage.setItem(`tenant_name_${tenantId}`, tenant.name);
    }
  }, [tenant?.name, tenantId]);

  // 動態更新 favicon（從 tenant 資料中讀取）
  useEffect(() => {
    const updateFavicon = (url: string | null | undefined) => {
      // 移除現有的 favicon 連結
      const existingLinks = document.querySelectorAll('link[rel*="icon"]');
      existingLinks.forEach(link => link.remove());

      if (url) {
        const normalizedUrl = normalizeImageUrl(url);
        if (normalizedUrl) {
          // 創建新的 favicon 連結
          const link = document.createElement('link');
          link.rel = 'icon';
          link.type = 'image/webp';
          link.href = normalizedUrl;
          document.head.appendChild(link);
          return;
        }
      }

      // 如果沒有 favicon，恢復預設的 favicon
      const defaultFavicon = document.createElement('link');
      defaultFavicon.rel = 'icon';
      defaultFavicon.type = 'image/svg+xml';
      defaultFavicon.href = '/icons/favicon.svg';
      document.head.appendChild(defaultFavicon);
    };

    const tenantFaviconUrl = (tenant as any)?.favicon_url;
    updateFavicon(tenantFaviconUrl);
  }, [tenant]);

  // 嘗試從 localStorage 讀取緩存的 tenant 名稱（登出後使用）
  const cachedTenantName = useMemo(() => {
    if (tenantId) {
      return localStorage.getItem(`tenant_name_${tenantId}`);
    }
    return null;
  }, [tenantId]);

  // 顯示商家名稱，優先使用實際的 tenant 名稱，如果沒有則使用緩存的名稱
  // 如果 API 還在載入或失敗，暫時顯示預設值（但應該會從 API 獲取到實際名稱）
  const displayName =
    tenant?.name ||
    cachedTenantName ||
    (isStaffSession ? 'POS 系統' : '商家名稱');

  // 處理 Logo URL
  const tenantLogoUrl = tenant?.logo_url
    ? normalizeImageUrl(tenant.logo_url)
    : undefined;

  // 當 logo_url 改變時，重置 logoError 狀態
  useEffect(() => {
    if (tenantLogoUrl) {
      setLogoError(false);
    }
  }, [tenantLogoUrl]);

  const hasLogo = !!tenantLogoUrl && !logoError;

  return (
    <div
      className="h-full flex items-center gap-2 px-4 cursor-pointer hover:opacity-80 transition-opacity"
      onClick={onNavigate}
    >
      {hasLogo && (
        <img
          src={tenantLogoUrl}
          alt={`${displayName} Logo`}
          className="h-8 max-h-8 object-contain"
          onError={_e => {
            // Logo 載入失敗時，隱藏 Logo，只顯示商家名稱
            setLogoError(true);
          }}
        />
      )}
      <span className="text-white font-bold text-lg whitespace-nowrap">
        {displayName}
      </span>
    </div>
  );
}

// SWR 不需要全域 Provider，錯誤處理在 fetcher 中統一處理

/**
 * 路由類型定義
 */
type Route =
  | 'home'
  | 'data'
  | 'admin'
  | 'pos'
  | 'redeem'
  | 'tenant-register'
  | 'tenant-select'
  | 'invite-accept'
  | 'staff-setup'
  | 'tenant-settings';

/**
 * 簡單路由系統
 */
export function Router() {
  // 獲取當前用戶的後台訪問權限
  const { hasAccess, isLoading: isRoleLoading } = useHasAdminAccess();
  // PIN 登入後目前身份為 staff，不顯示後台連結（與 role 無關）
  const isStaffSession =
    typeof window !== 'undefined' &&
    !!localStorage.getItem('staff_session_token');

  // 從 URL 獲取當前路由
  const getCurrentRouteFromURL = (): Route => {
    const path = window.location.pathname;
    // 處理路徑重複的情況（例如 /tenant-register/tenant-register）
    const normalizedPath = path
      .replace(/\/tenant-register\/tenant-register/g, '/tenant-register')
      .replace(/\/tenant-settings\/tenant-settings/g, '/tenant-settings');

    // 簡短 URL 重定向（Vanity URL）
    // /join 和 /code 都重定向到邀請接受頁面
    if (normalizedPath === '/join' || normalizedPath === '/code') {
      window.history.replaceState({}, '', '/invite/accept');
      return 'invite-accept';
    }

    // 優先檢查特定路由（避免被其他路由匹配）
    if (normalizedPath === '/tenant-select' || path.includes('/tenant-select'))
      return 'tenant-select';
    if (normalizedPath === '/invite/accept' || path.includes('/invite/accept'))
      return 'invite-accept';
    if (
      normalizedPath === '/tenant-register' ||
      normalizedPath === '/register' ||
      path.includes('/tenant-register')
    )
      return 'tenant-register';
    if (normalizedPath === '/staff-setup' || path.includes('/staff-setup'))
      return 'staff-setup';
    if (
      normalizedPath === '/tenant-settings' ||
      normalizedPath === '/settings' ||
      path.includes('/tenant-settings')
    )
      return 'tenant-settings';

    if (normalizedPath === '/data' || path.startsWith('/data/')) return 'data';
    if (normalizedPath === '/admin' || path.startsWith('/admin/'))
      return 'admin';
    if (normalizedPath === '/pos' || path.startsWith('/pos/')) return 'pos';
    if (normalizedPath === '/redeem' || path.startsWith('/redeem/'))
      return 'redeem';
    return 'home';
  };

  const [currentRoute, setCurrentRoute] = useState<Route>(() =>
    getCurrentRouteFromURL()
  );

  // 具備 token 與 tenant_id 時於 mount 時呼叫 Bootstrap 並 hydrate SWR（非 tenant-select 頁）
  useBootstrapOnMount();

  // 檢查 STAFF 角色訪問後台的權限
  useEffect(() => {
    if (!isRoleLoading && currentRoute === 'admin' && !hasAccess) {
      // STAFF 嘗試訪問後台，重定向到 POS
      toast.error('您沒有權限訪問後台管理頁面');
      setCurrentRoute('pos');
      window.history.replaceState({}, '', '/pos');
    }
  }, [currentRoute, hasAccess, isRoleLoading]);

  /**
   * 監聽 URL 變化
   */
  useEffect(() => {
    const handlePopState = () => {
      setCurrentRoute(getCurrentRouteFromURL());
    };

    // 監聽瀏覽器前進/後退
    window.addEventListener('popstate', handlePopState);

    // 監聽 hashchange（用於處理 SPA 路由變化）
    window.addEventListener('hashchange', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, []);

  /**
   * 導航函式
   */
  const navigate = (route: Route) => {
    // 檢查 STAFF 角色訪問後台的權限
    if (route === 'admin' && !isRoleLoading && !hasAccess) {
      toast.error('您沒有權限訪問後台管理頁面');
      // 重定向到 POS
      setCurrentRoute('pos');
      window.history.pushState({}, '', '/pos');
      return;
    }

    setCurrentRoute(route);
    const path = route === 'home' ? '/' : `/${route}`;
    window.history.pushState({}, '', path);
  };

  /**
   * 渲染當前路由
   */
  const renderRoute = () => {
    switch (currentRoute) {
      case 'data':
        return <Data />;
      case 'admin':
        return <AdminLayout />;
      case 'pos':
        return <POSPage />;
      case 'redeem':
        return <RedeemPage />;
      case 'tenant-register':
        return <TenantRegistration />;
      case 'tenant-select':
        return <TenantSelect />;
      case 'invite-accept':
        return <InviteAccept />;
      case 'staff-setup':
        return <StaffSetup />;
      case 'home':
      default:
        return <App />;
    }
  };

  // 判斷是否顯示導航列
  // 在 home、tenant-select、tenant-register、invite-accept、staff-setup 頁面不顯示導航列（這些是過渡頁面或 landing page）
  const showNavbar = ![
    'home',
    'tenant-select',
    'tenant-register',
    'invite-accept',
    'staff-setup',
  ].includes(currentRoute);

  return (
    <>
      <ErrorBoundary>
        <div className="min-h-screen">
          {/* 條件式顯示導航列（landing page 不顯示） */}
          {showNavbar && (
            <nav className="bg-gray-800 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                  {/* 左側：Logo、後台、POS */}
                  <div className="flex items-center space-x-6">
                    {/* Logo */}
                    <LogoImage
                      onNavigate={() =>
                        navigate(isStaffSession || !hasAccess ? 'pos' : 'admin')
                      }
                    />
                    {/* 只有 OWNER/MANAGER 且非 staff session（PIN 登入）時顯示後台；已在 admin 頁時載入中也顯示，避免閃爍 */}
                    {!isStaffSession &&
                      (hasAccess ||
                        (currentRoute === 'admin' && isRoleLoading)) && (
                        <button
                          onClick={() => navigate('admin')}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            currentRoute === 'admin'
                              ? 'bg-gray-900 text-white'
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                        >
                          後台
                        </button>
                      )}
                    {/* 僅 staff session（PIN 登入）時顯示 POS、核銷；Google OAuth 登入者只看得到後台 */}
                    {isStaffSession && (
                      <>
                        <button
                          onClick={() => navigate('pos')}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            currentRoute === 'pos'
                              ? 'bg-gray-900 text-white'
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                        >
                          POS
                        </button>
                        <button
                          onClick={() => navigate('redeem')}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            currentRoute === 'redeem'
                              ? 'bg-gray-900 text-white'
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                        >
                          核銷
                        </button>
                      </>
                    )}
                  </div>

                  {/* 右側：商家登入、社群連結（Jason Lin 按鈕右邊） */}
                  <div className="flex items-center gap-2">
                    <UserMenu />
                    <a
                      href="https://discord.gg/44Scw4DB"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Discord 討論區"
                      className="flex items-center justify-center rounded-full p-2 text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500"
                    >
                      <SiDiscord size={20} color="currentColor" />
                    </a>
                    <a
                      href="https://www.instagram.com/emvyax"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="flex items-center justify-center rounded-full p-2 text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500"
                    >
                      <SiInstagram size={20} color="currentColor" />
                    </a>
                  </div>
                </div>
              </div>
            </nav>
          )}

          {/* 路由內容 - 根據是否有導航列調整 padding */}
          <div className={showNavbar ? 'pt-16' : ''}>{renderRoute()}</div>
        </div>
      </ErrorBoundary>
    </>
  );
}
